import React, { useContext, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Container,
  Stack
} from '@mui/material'
import {
  PeopleAlt,
  ShoppingCart,
  AttachMoney,
  HourglassEmpty,
  Dashboard as DashboardIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { AccountContext } from 'contexts/AccountContext'
import { getDashboardStats, DashboardStats } from 'api/accounts'
import { getOrderStatusColor } from 'utils/orderConstants'

const DashboardPage = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useContext(AccountContext)
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchStats = async () => {
      if (!isAuthenticated) {
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        const data = await getDashboardStats()
        setStats(data)
        setError(null)
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load dashboard data')
      } finally {
        setIsLoading(false)
      }
    }

    fetchStats()
  }, [isAuthenticated])

  const handleViewPendingAccounts = () => {
    navigate('/accounts?status=Pending')
  }

  const handleViewTotalAccounts = () => {
    navigate('/accounts?status=Approved')
  }

  const handleViewTotalOrders = () => {
    navigate('/orders')
  }

  const handleViewTotalRevenue = () => {
    navigate('/orders')
  }

  const handleViewOrdersByStatus = () => {
    navigate('/orders')
  }

  const handleViewRecentOrders = () => {
    navigate('/orders')
  }

  const formatStatus = (status: string) => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (!isAuthenticated) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity='info' sx={{ mb: 2 }}>
          You are not logged in. Please log in to access admin features.
        </Alert>
      </Container>
    )
  }

  if (isLoading) {
    return (
      <Container sx={{ py: 4 }}>
        <Box display='flex' justifyContent='center'>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity='error'>{error}</Alert>
      </Container>
    )
  }

  if (!stats) {
    return null
  }

  return (
    <Container sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Stack direction='row' alignItems='center' spacing={2} sx={{ mb: 3 }}>
          <DashboardIcon fontSize='large' color='primary' />
          <Typography variant='h4'>Dashboard</Typography>
        </Stack>

        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              onClick={handleViewTotalAccounts}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s'
                }
              }}
            >
              <CardContent>
                <Box display='flex' alignItems='center' mb={1}>
                  <PeopleAlt
                    sx={{ fontSize: 40, color: 'primary.main', mr: 2 }}
                  />
                  <Box>
                    <Typography color='textSecondary' variant='body2'>
                      Total Accounts
                    </Typography>
                    <Typography variant='h4'>{stats.accounts.total}</Typography>
                  </Box>
                </Box>
                <Typography variant='body2' color='textSecondary'>
                  Approved: {stats.accounts.approved}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              onClick={handleViewPendingAccounts}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s'
                }
              }}
            >
              <CardContent>
                <Box display='flex' alignItems='center' mb={1}>
                  <HourglassEmpty
                    sx={{ fontSize: 40, color: 'warning.main', mr: 2 }}
                  />
                  <Box>
                    <Typography color='textSecondary' variant='body2'>
                      Pending Accounts
                    </Typography>
                    <Typography variant='h4'>
                      {stats.accounts.pending}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant='body2' color='textSecondary'>
                  Awaiting approval
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              onClick={handleViewTotalOrders}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s'
                }
              }}
            >
              <CardContent>
                <Box display='flex' alignItems='center' mb={1}>
                  <ShoppingCart
                    sx={{ fontSize: 40, color: 'info.main', mr: 2 }}
                  />
                  <Box>
                    <Typography color='textSecondary' variant='body2'>
                      Total Orders
                    </Typography>
                    <Typography variant='h4'>{stats.orders.total}</Typography>
                  </Box>
                </Box>
                <Typography variant='body2' color='textSecondary'>
                  Paid: {stats.orders.byStatus.PAID || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              onClick={handleViewTotalRevenue}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s'
                }
              }}
            >
              <CardContent>
                <Box display='flex' alignItems='center' mb={1}>
                  <AttachMoney
                    sx={{ fontSize: 40, color: 'success.main', mr: 2 }}
                  />
                  <Box>
                    <Typography color='textSecondary' variant='body2'>
                      Total Revenue
                    </Typography>
                    <Typography variant='h4'>
                      {formatCurrency(stats.revenue.paid)}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant='body2' color='textSecondary'>
                  Paid orders only
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              onClick={handleViewOrdersByStatus}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s'
                }
              }}
            >
              <CardContent>
                <Typography variant='h6' mb={2}>
                  Orders by Status
                </Typography>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell align='right'>Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(stats.orders.byStatus).map(
                        ([status, count]) => (
                          <TableRow key={status}>
                            <TableCell>
                              <Chip
                                label={formatStatus(status)}
                                color={getOrderStatusColor(status)}
                                size='small'
                              />
                            </TableCell>
                            <TableCell align='right'>{count}</TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              onClick={handleViewRecentOrders}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s'
                }
              }}
            >
              <CardContent>
                <Typography variant='h6' mb={2}>
                  Recent Orders
                </Typography>
                <TableContainer component={Paper} variant='outlined'>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order ID</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align='right'>Amount</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.orders.recent.length > 0 ? (
                        stats.orders.recent.map(order => (
                          <TableRow key={order.id}>
                            <TableCell>{order.salesOrderId || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={formatStatus(order.status)}
                                color={getOrderStatusColor(order.status)}
                                size='small'
                              />
                            </TableCell>
                            <TableCell align='right'>
                              {formatCurrency(order.amount)}
                            </TableCell>
                            <TableCell>{formatDate(order.createdAt)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align='center'>
                            No recent orders
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  )
}

export default DashboardPage
